import React, { useEffect, useMemo, useState } from "react";
import ReactTable from "../../reusableComponents/ReactTable/ReactTable";
import axios from "axios";
import { baseUrl } from "../../utils/api/apis";
import { useSelector } from "react-redux";
import LoaderOverlay from "../Loader";
import { country } from "../../utils/constants/country";
import { Tooltip, DatePicker, Button, message, Switch } from "antd";
import ReactCountryFlag from "react-country-flag";
import { FormatUSD } from "../../utils/string";
import moment from "moment";

const { RangePicker } = DatePicker;

const PurchaseReports = () => {
  const { idToken } = useSelector((state) => state.auth);

  const [reports, setReports] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isBST, setIsBST] = useState(true); // true = BST (UTC+1), false = UTC

  // antd date range (dayjs objects)
  const [dateRange, setDateRange] = useState(null);

  // pagination params for server-side table
  const [params, setParams] = useState({
    page: 1,
    page_size: 10,
  });

  // Define columns for ReactTable (serverSide mode)
  const columns = useMemo(
    () => [
      {
        Header: "Country",
        accessor: "Country",
        Cell: ({ value }) => {
          const selectedCountry = country?.find(
            (c) => c?.country === value || c?.iso === value
          );
          return (
            <>
              {" "}
              <ReactCountryFlag
                countryCode={selectedCountry?.iso}
                svg
                style={{ width: "2em", height: "2em" }}
                title={selectedCountry?.country || "N/A"}
                aria-label={selectedCountry?.country || "N/A"}
              />{" "}
              {selectedCountry?.country}
            </>
          );
        },
      },

      // 🔥 NEW FIELDS YOU REQUESTED
      {
        Header: "Stage 1",
        accessor: "stage1",
      },
      {
        Header: "Stage 1 Status",
        accessor: "stage1_status",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Stage 2",
        accessor: "stage2",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Stage 2 Status",
        accessor: "stage2_status",
        Cell: ({ value }) => value || "-",
      },

      // existing ones
      {
        Header: "Funded / Proedge",
        accessor: "funded_proedge",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Funded Status",
        accessor: "funded_proedge_status",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Challenge",
        accessor: "challenge",
      },
      {
        Header: "Plan",
        accessor: "plan",
      },
      {
        Header: "Code",
        accessor: "code",
      },
      {
        Header: "Purchase Price",
        accessor: "purchase_price",
        Cell: ({ value }) => (value ? <span>{FormatUSD(value)}</span> : "-"),
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Invoice ID",
        accessor: "invoice_id",
      },
      {
        Header: "Payment Mode",
        accessor: "payment_mode",
        Cell: ({ value }) =>
          value ? (
            <span style={{ textTransform: "capitalize" }}>{value}</span>
          ) : (
            "-"
          ),
      },
      {
        Header: "Platform",
        accessor: "platform",
        Cell: ({ value }) =>
          value ? (
            <span style={{ textTransform: "capitalize" }}>{value}</span>
          ) : (
            "-"
          ),
      },
      {
        Header: "Issue Type",
        accessor: "issue_type",
      },
      {
        Header: "Purchase Date",
        accessor: "purchase_date",
        Cell: ({ value }) => {
          if (!value) return "-";
          const m = moment.utc(value);
          const display = isBST ? m.utcOffset("+01:00") : m;
          return display.format("YYYY/MM/DD hh:mm:ss a");
        },
      },
      {
        Header: "Created Date",
        accessor: "created_date",
        Cell: ({ value }) => {
          if (!value) return "-";
          const m = moment.utc(value);
          const display = isBST ? m.utcOffset("+01:00") : m;
          return display.format("YYYY/MM/DD hh:mm:ss a");
        },
      },
    ],
    [isBST] // re-derive when timezone toggle changes
  );

  const fetchReports = async (currentParams = params) => {
    if (!idToken) return;
    setLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${idToken}` },
        params: {
          page: currentParams.page,
          page_size: currentParams.page_size,
        },
      };

      // include date filters for normal listing as well
      if (dateRange && dateRange[0] && dateRange[1]) {
        config.params.start_date = dateRange[0].format("YYYY-MM-DD");
        config.params.end_date = dateRange[1].format("YYYY-MM-DD");
      }

      const response = await axios.get(
        `${baseUrl}payment/admin/ee-purchase-report/`,
        config
      );

      // Handle common API response shapes
      let rows = [];
      let count = 0;

      if (Array.isArray(response.data)) {
        rows = response.data;
        count = response.data.length;
      } else if (Array.isArray(response.data.results)) {
        rows = response.data.results;
        count = response.data.count ?? response.data.results.length;
      } else if (Array.isArray(response.data.data)) {
        rows = response.data.data;
        count = response.data.total ?? response.data.data.length;
      } else {
        rows = [];
        count = 0;
      }

      setReports(rows);
      setTotalCount(count);
    } catch (error) {
      console.error("Error fetching purchase reports:", error);
    } finally {
      setLoading(false);
    }
  };

// --- Helper function (put this outside your component) ---
const downloadBlob = (data, filename, type) => {
  const blob = new Blob([data], { type });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  
  // Append to body, click, and clean up
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
// ---------------------------------------------------------

const handleExportCsv = async () => {
  if (!idToken) return;

  if (!dateRange || !dateRange[0] || !dateRange[1]) {
    message.warning("Please select a date range before exporting.");
    return;
  }

  const start_date = dateRange[0].format("YYYY-MM-DD");
  const end_date = dateRange[1].format("YYYY-MM-DD");

  setExportLoading(true);
  try {
    const response = await axios.get(
      `${baseUrl}payment/admin/ee-purchase-report/`,
      {
        headers: { Authorization: `Bearer ${idToken}` },
        params: {
          export: "csv",
          start_date,
          end_date,
        },
        responseType: "blob",
      }
    );

    const filename = `purchase-report-${start_date}-to-${end_date}.csv`;
    downloadBlob(response.data, filename, "text/csv;charset=utf-8;");

  } catch (error) {
    console.error("Error exporting CSV purchase reports:", error);
    message.error("Failed to export CSV.");
  } finally {
    setExportLoading(false);
  }
};

  // Fetch whenever page or page_size or dateRange changes
  useEffect(() => {
    fetchReports(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page, params.page_size, idToken, dateRange]);

  return (
    <div className="table_scroll_wrapper">
      <div className="alltable">
        <div className="table_wrapper">
          {/* Filter + Export bar */}
          <div
            className="reports_filters"
            style={{
              marginBottom: 16,
              display: "flex",
              gap: 12,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <span>Date Range:</span>
            <RangePicker
              value={dateRange}
              onChange={(val) => setDateRange(val)}
              allowClear
            />
            <Button
              type="primary"
              onClick={handleExportCsv}
              loading={exportLoading}
            >
              Export CSV
            </Button>

            {/* Timezone toggle */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginLeft: "auto",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 8,
                padding: "4px 12px",
              }}
            >
              <span style={{ fontWeight: 500, fontSize: 13, opacity: 0.8 }}>UTC</span>
              <Switch
                checked={isBST}
                onChange={(checked) => setIsBST(checked)}
                size="small"
              />
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: isBST ? "#4ade80" : "inherit",
                  transition: "color 0.2s",
                }}
              >
                BST
              </span>
            </div>
          </div>

          <div className="table_scroll_wrapper">
            {loading && <LoaderOverlay />}
            <ReactTable
              columns={columns}
              data={reports}
              serverSide={true}
              params={params}
              setParams={setParams}
              totalCount={totalCount}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseReports;
